import React, {useState, useCallback, useImperativeHandle, forwardRef, useEffect, useRef} from 'react'
import {Box} from '@mui/material'
import {v4 as uuidv4} from 'uuid'
import MessageList from './MessageList'
import MessageInput, { type MessageInputHandle } from './MessageInput'
import ContextWindowBar from './ContextWindowBar'
import SiteBuilderOptionsWidget from './SiteBuilderOptionsWidget'
import SkillSelectionWidget from './SkillSelectionWidget'
import DiscoveryQuestionWidget from './DiscoveryQuestionWidget'
import ObjectivesWidget from './ObjectivesWidget'
import {useCrosstabsState, type CrosstabsTab} from './useCrosstabsState'
import {useVoiceInput} from './useVoiceInput'
import {uiSounds} from './uiSounds'
import {approvePlanAndStartBuilding} from './planApproval'
import {usePostHog} from '../../contexts/PostHogProvider'
import {useOptionalUserIdentity} from '../../contexts/UserIdentityContext'
import type {AgentModelData, AppArtifactBuilderData, AppArtifactOpenData, AuthoringIterationData, AuthoringVerifyData, CatalogModelOption, ChatAttachment, ChatMessage, ChatService, ComposerDraftStore, CrosstabsClassificationData, CrosstabsFileRequestData, CrosstabsObjectivesData, CrosstabsOptionsData, CrosstabsPlanData, DiscoveryQuestionData, IntentResult, LocationRequestData, MarkdownArtifactPanelData, McpAppData, McpAppInputData, MicroAppOpenData, ProgressStepData, ProgressStepUpdateData, SiteBuilderCodeChunkData, SiteBuilderOptionsData, SiteBuilderPlanData, SkillLoadedData, SkillSelectionData, TokenUsage, WebsiteChangeSetReadyData, WebsiteEditContext, WebsiteTargetReference} from './types'
import {appendAssistantChunk, createProvisionalPlanningStep, finalizeRunningProgressSteps, mergeProgressStepUpdate, replaceProvisionalPlanningStep, restoreChatMessages, type RestoredChatMessage} from './activity'

export interface R3SendMessageOptions {
  websiteEditContext?: WebsiteEditContext
  websiteTargetReferences?: WebsiteTargetReference[]
  draftStore?: ComposerDraftStore
  pendingFileScope?: 'main' | 'contextual'
}

interface PendingChatFile {
  clientId: string
  name: string
  id?: string
  uploading: boolean
  mimeType?: string
  sizeBytes?: number
  url?: string
  scope: 'main' | 'contextual'
}


export interface R3ChatHandle {
  reset: () => void
  sendMessage: (text: string, options?: R3SendMessageOptions) => void
  attachFiles: (files: File[], options?: {scope?: 'main' | 'contextual'}) => void
  startVoice: (options?: {draftStore?: ComposerDraftStore}) => void
  acceptVoice: (options?: {draftStore?: ComposerDraftStore}) => void
  cancelVoice: () => void
  /**
   * Resume a previous session by loading its messages into the chat.
   * When `inProgressTurn.status === 'running'`, the chat reattaches to the live turn
   * (GET /api/r3/chat/stream): the server replays the turn's event log, the streaming
   * indicator restores, and tokens continue live.
   */
  loadSession: (sessionId: string, messages: RestoredChatMessage[], contextInfo?: {contextTokens: number; contextWindow: number}, inProgressTurn?: {status: 'running' | 'complete' | 'error' | 'interrupted'} | null) => void
}

interface R3ChatProps {
  chatService: ChatService
  onSessionCreated?: (sessionId: string) => void
  reachyAvatarUrl?: string
  /** First name for the empty-state hero greeting ("Good afternoon, {name}."). */
  userName?: string
  headerActions?: React.ReactNode
  onMessagesChange?: (messages: Array<{role: 'user' | 'assistant'; content: string}>) => void
  onSessionInfoChange?: (info: {intentSummary?: string; startedAt?: number}) => void
  onSiteBuilderPlan?: (data: SiteBuilderPlanData) => void
  onSiteBuilderGenerating?: (siteId: string | number) => void
  onSiteBuilderCodeChunk?: (data: SiteBuilderCodeChunkData) => void
  onSiteBuilderPreview?: (renderHtml: string, siteId: string | number, revision?: number, htmlContent?: string) => void
  onSiteBuilderPublished?: (url: string, siteId: string | number) => void
  onWebsiteChangeSetReady?: (data: WebsiteChangeSetReadyData) => void
  onMarkdownArtifactPanel?: (data: MarkdownArtifactPanelData) => void
  onStreamingAssistantContent?: (content: string | null) => void
  /** Fires whenever the turn-in-flight state flips — true from the moment of SEND (not first
   *  token), false when the turn settles. The shell's cross-tab/refocus re-sync gates on this:
   *  it must never rebuild the chat while THIS instance owns a live turn. */
  onStreamingChange?: (isStreaming: boolean) => void
  onCrosstabsClassification?: (data: CrosstabsClassificationData) => void
  onCrosstabsObjectives?: (data: CrosstabsObjectivesData) => void
  onCrosstabsPlan?: (data: CrosstabsPlanData) => void
  onCrosstabsPanelOpen?: (tab: CrosstabsTab) => void
  onMcpApp?: (data: McpAppData) => void
  onAppArtifactOpen?: (data: AppArtifactOpenData) => void
  onMicroAppOpen?: (data: MicroAppOpenData) => void
  onLocationRequest?: (data: LocationRequestData) => void
  onDownloadAppArtifactBuilder?: (data: AppArtifactBuilderData) => Promise<void>
  apiBaseUrl?: string
  sessionId?: string
  devMode?: boolean
  onApprovePlan?: (sessionId: string) => Promise<void>
  uploadFile?: (sessionId: string, file: File, signal?: AbortSignal) => Promise<string>
  fetchVoiceToken?: () => Promise<string>
  /** Model shown before the first turn resolves its active agent. */
  initialModelLabel?: string
  /** Dev-mode model picker catalog (see ChatWorkspace's dev-mode-gated fetch). Omit to keep the
   *  model label a static, non-interactive display. */
  availableModels?: CatalogModelOption[]
  /** Sets this session's model override (POST /api/r3/session-tag under the hood, implemented by
   *  the parent since it owns the auth token). Only called from the dev-mode picker. */
  onSetModelOverride?: (sessionId: string, spec: string) => Promise<void>
  /** Website edit context is snapshotted with each send; it is not embedded in visible text. */
  websiteEditContext?: WebsiteEditContext
  websiteTargetReferences?: WebsiteTargetReference[]
  websiteTargetChips?: React.ReactNode
  composerDraftStore?: ComposerDraftStore
  onPendingFilesChange?: (files: PendingChatFile[]) => void
  onVoiceStateChange?: (state: {status: 'idle' | 'connecting' | 'recording' | 'stopping'; transcript: string; destination: 'main' | 'external'}) => void
}

/** Cross-tab channel: R3Chat announces "a turn just started on session X" so OTHER tabs showing
 *  that session can reattach to the live turn (via the shell's resume path) instead of sitting
 *  stale until a manual refresh. The posting tab never receives its own message (BroadcastChannel
 *  spec), so there is no self-trigger to guard against. Best-effort: environments without
 *  BroadcastChannel (older Electron, tests) just skip it. */
export const ROS_TURN_CHANNEL = 'ros-chat-turns'
function broadcastTurnStarted(sessionId: string) {
  if (typeof BroadcastChannel === 'undefined') return
  try {
    const ch = new BroadcastChannel(ROS_TURN_CHANNEL)
    ch.postMessage({type: 'turn-started', sessionId})
    ch.close()
  } catch { /* best-effort */ }
}

function isComposerDraftStore(value: unknown): value is ComposerDraftStore {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<ComposerDraftStore>
  return typeof candidate.getSnapshot === 'function'
    && typeof candidate.setValue === 'function'
    && typeof candidate.subscribe === 'function'
}

const R3Chat = forwardRef<R3ChatHandle, R3ChatProps>(({chatService, onSessionCreated, reachyAvatarUrl, userName, headerActions, onMessagesChange, onSessionInfoChange, onSiteBuilderPlan: onSiteBuilderPlanProp, onSiteBuilderGenerating, onSiteBuilderCodeChunk, onSiteBuilderPreview, onSiteBuilderPublished, onWebsiteChangeSetReady, onMarkdownArtifactPanel, onStreamingAssistantContent, onStreamingChange, onCrosstabsClassification: onCrosstabsClassificationProp, onCrosstabsObjectives: onCrosstabsObjectivesProp, onCrosstabsPlan: onCrosstabsPlanProp, onCrosstabsPanelOpen, onMcpApp: onMcpAppProp, onAppArtifactOpen: onAppArtifactOpenProp, onMicroAppOpen: onMicroAppOpenProp, onLocationRequest: onLocationRequestProp, onDownloadAppArtifactBuilder, apiBaseUrl, sessionId: externalSessionId, devMode, onApprovePlan, uploadFile, fetchVoiceToken, initialModelLabel, availableModels, onSetModelOverride, websiteEditContext, websiteTargetReferences, websiteTargetChips, composerDraftStore, onPendingFilesChange, onVoiceStateChange}, ref) => {
  const {trackEvent} = usePostHog()
  // Optional: present in Studio (drives department-tagged tracking/chat), absent in standalone Ros
  const department = useOptionalUserIdentity()?.department ?? null

  const [messages, setMessages] = useState<ChatMessage[]>([])
  // PERF: the draft text is NOT React state here — it lives inside MessageInput (uncontrolled), so
  // typing re-renders only the composer, never R3Chat/MessageList. When the draft was lifted state,
  // every keystroke re-rendered the whole tree and (via identity-unstable callbacks) re-parsed every
  // message's markdown — the long-chat typing lag, confirmed by CPU profile. R3Chat keeps:
  //  - inputValueRef: a passive mirror (via onDraftChange) that handleSend reads at send time
  //  - inputApiRef:   an imperative handle for voice-insert / clear-after-send / reset
  const inputValueRef = useRef('')
  const inputApiRef = useRef<MessageInputHandle | null>(null)
  const handleDraftChange = useCallback((v: string) => { inputValueRef.current = v }, [])
  const setInputValue = useCallback((v: string) => {
    inputValueRef.current = v                 // keep the mirror true even if no composer is mounted
    composerDraftStore?.setValue(v)
    inputApiRef.current?.setValue(v)
  }, [composerDraftStore])
  const [isStreaming, setIsStreaming] = useState(false)
  useEffect(() => { onStreamingChange?.(isStreaming) }, [isStreaming, onStreamingChange])
  const [sessionId, setSessionId] = useState<string | undefined>(externalSessionId)
  const [contextInfo, setContextInfo] = useState({contextTokens: 0, contextWindow: 200_000, compacted: false})
  const [activeOptions, setActiveOptions] = useState<SiteBuilderOptionsData | null>(null)
  const [activeDiscoveryQuestion, setActiveDiscoveryQuestion] = useState<DiscoveryQuestionData | null>(null)
  const [activeSkillSelection, setActiveSkillSelection] = useState<SkillSelectionData | null>(null)
  const [pendingFiles, setPendingFiles] = useState<PendingChatFile[]>([])
  const localAttachmentUrlsRef = useRef(new Set<string>())
  const uploadControllersRef = useRef(new Map<string, AbortController>())
  const releaseLocalAttachmentUrl = useCallback((url?: string) => {
    if (!url || !localAttachmentUrlsRef.current.delete(url)) return
    URL.revokeObjectURL(url)
  }, [])
  const clearPendingUploads = useCallback(() => {
    uploadControllersRef.current.forEach(controller => controller.abort())
    uploadControllersRef.current.clear()
    setPendingFiles(previous => {
      previous.forEach(file => releaseLocalAttachmentUrl(file.url))
      return []
    })
  }, [releaseLocalAttachmentUrl])
  const voiceDraftStoreRef = useRef<ComposerDraftStore | undefined>()
  const [voiceDestination, setVoiceDestination] = useState<'main' | 'external'>('main')
  useEffect(() => { onPendingFilesChange?.(pendingFiles) }, [pendingFiles, onPendingFilesChange])
  useEffect(() => () => {
    uploadControllersRef.current.forEach(controller => controller.abort())
    uploadControllersRef.current.clear()
    localAttachmentUrlsRef.current.forEach(url => URL.revokeObjectURL(url))
    localAttachmentUrlsRef.current.clear()
  }, [])
  const streamingContentRef = useRef('')
  const streamingMsgIdRef = useRef<string | null>(null)
  // Track skills already tracked this session so r3_chat_skill_loaded fires once per skill, not per turn.
  const trackedLoadedSkillsRef = useRef<Set<string>>(new Set())
  const [activeAgent, setActiveAgent] = useState<string | undefined>(undefined)
  const [activeModel, setActiveModel] = useState<AgentModelData | null>(null)
  // Model identity is an implementation detail per the agent-model-catalog decision (provider/model
  // is never a second control for end users), so only surface it in dev mode, for debugging which
  // model actually produced a given reply.
  const devModelLabel = devMode ? (activeModel ? `Model: ${activeModel.label}` : initialModelLabel) : undefined
  // Dev-mode-only: set this session's model override, then update the badge immediately rather than
  // waiting for the next turn's agent_model SSE event to confirm it (that event still fires and will
  // just re-confirm the same value).
  const handleSelectModel = useCallback((spec: string) => {
    if (!sessionId || !onSetModelOverride) return
    const picked = availableModels?.find(m => m.spec === spec)
    void onSetModelOverride(sessionId, spec)
    setActiveModel({agentSlug: activeAgent ?? '', label: picked?.label ?? spec, spec, didFallback: false})
  }, [sessionId, onSetModelOverride, availableModels, activeAgent])
  const [pendingMcpAppInput, setPendingMcpAppInput] = useState<McpAppInputData | null>(null)
  // mcpModelContextRef removed — two-step context deferral now handled inside McpAppContainer

  const {state: crosstabsState, callbacks: crosstabsCallbacks, dismissOptions: dismissCrosstabsOptions, dismissObjectives: dismissCrosstabsObjectives, dismissFileRequest: dismissCrosstabsFileRequest, clearProgress: clearCrosstabsProgress, reset: resetCrosstabs} = useCrosstabsState(onCrosstabsPanelOpen)

  const appendToDraftStore = useCallback((store: ComposerDraftStore, text: string) => {
    const current = store.getSnapshot()
    store.setValue(current ? `${current} ${text}` : text)
  }, [])
  // Voice input — on unexpected WS close, preserve transcribed text in input
  const voiceOnTranscribed = useCallback((text: string) => {
    const destination = voiceDraftStoreRef.current
    if (destination && destination !== composerDraftStore) appendToDraftStore(destination, text)
    else setInputValue(text)
    voiceDraftStoreRef.current = undefined
    setVoiceDestination('main')
  }, [appendToDraftStore, composerDraftStore, setInputValue])
  const voiceOnError = useCallback((message: string) => {
    console.error('[R3Chat] Voice input error:', message)
    voiceDraftStoreRef.current = undefined
    setVoiceDestination('main')
  }, [])
  const noopFetchToken = useCallback(async () => { throw new Error('Voice input not configured') }, [])
  const {status: voiceStatus, transcript: voiceTranscript, audioLevelRef: voiceLevelRef, start: startVoice, accept: acceptVoice, cancel: cancelVoice} = useVoiceInput({
    fetchToken: fetchVoiceToken ?? noopFetchToken,
    onTranscribed: voiceOnTranscribed,
    onError: voiceOnError
  })
  useEffect(() => { onVoiceStateChange?.({status: voiceStatus, transcript: voiceTranscript, destination: voiceDestination}) }, [voiceStatus, voiceTranscript, voiceDestination, onVoiceStateChange])

  // A failed token request, denied microphone permission, or an empty unexpected WebSocket close
  // returns the recorder to idle without producing a transcript. Do not leave the main composer
  // permanently disabled because a contextual website composer was the last voice destination.
  useEffect(() => {
    if (voiceStatus !== 'idle' || voiceDestination === 'main') return
    voiceDraftStoreRef.current = undefined
    setVoiceDestination('main')
  }, [voiceStatus, voiceDestination])

  // Defensive recovery for clients that already received the old event-leaking click handler via
  // HMR: stop that misrouted recording immediately and return voice ownership to the main composer.
  useEffect(() => {
    if (voiceDestination !== 'external' || isComposerDraftStore(voiceDraftStoreRef.current)) return
    cancelVoice()
    voiceDraftStoreRef.current = undefined
    setVoiceDestination('main')
  }, [cancelVoice, voiceDestination])

  useEffect(() => {
    if (!composerDraftStore) return
    const sync = () => { inputValueRef.current = composerDraftStore.getSnapshot() }
    sync()
    return composerDraftStore.subscribe(sync)
  }, [composerDraftStore])
  // Start recording (mic button) — plays a soft start cue.
  const handleMicToggle = useCallback((draftStore?: ComposerDraftStore) => {
    if (voiceStatus !== 'idle') return
    const destination = isComposerDraftStore(draftStore) ? draftStore : undefined
    voiceDraftStoreRef.current = destination
    setVoiceDestination(destination && destination !== composerDraftStore ? 'external' : 'main')
    trackEvent('r3_chat_voice_toggled', { user_department: department })
    uiSounds.recordStart()
    startVoice()
  }, [composerDraftStore, startVoice, voiceStatus])
  // ✓ accept — stop recording, drop the transcript into the input (editable), play accept cue.
  const handleVoiceAccept = useCallback((draftStore?: ComposerDraftStore) => {
    const text = acceptVoice()
    uiSounds.accept()
    if (text) {
      const requestedDestination = isComposerDraftStore(draftStore) ? draftStore : undefined
      const destination = requestedDestination ?? voiceDraftStoreRef.current
      if (destination && destination !== composerDraftStore) appendToDraftStore(destination, text)
      else if (inputApiRef.current) inputApiRef.current.appendValue(text)
      else setInputValue(inputValueRef.current ? `${inputValueRef.current} ${text}` : text)
    }
    voiceDraftStoreRef.current = undefined
    setVoiceDestination('main')
  }, [acceptVoice, appendToDraftStore, composerDraftStore, setInputValue])
  // ✕ reject — stop recording, discard, play reject cue.
  const handleVoiceCancel = useCallback(() => {
    cancelVoice()
    voiceDraftStoreRef.current = undefined
    setVoiceDestination('main')
    uiSounds.reject()
  }, [cancelVoice])

  useImperativeHandle(ref, () => ({
    reset: () => {
      chatService.cancel()
      streamingMsgIdRef.current = null
      trackedLoadedSkillsRef.current.clear()
      setActiveAgent(undefined)
      setActiveModel(null)
      setMessages([])
      setSessionId(undefined)
      setInputValue('')
      setIsStreaming(false)
      setContextInfo({contextTokens: 0, contextWindow: 200_000, compacted: false})
      setActiveOptions(null)
      setActiveDiscoveryQuestion(null)
      setActiveSkillSelection(null)
      clearPendingUploads()
      voiceDraftStoreRef.current = undefined
      setVoiceDestination('main')
      resetCrosstabs()
      onMessagesChange?.([])
      onSessionInfoChange?.({})
    },
    sendMessage: (text: string, options?: R3SendMessageOptions) => {
      handleSend(text, options)
    },
    attachFiles: (files: File[], options) => { void handleFileAttach(files, options?.scope) },
    startVoice: options => handleMicToggle(options?.draftStore),
    acceptVoice: options => handleVoiceAccept(options?.draftStore),
    cancelVoice: handleVoiceCancel,
    loadSession: (newSessionId: string, history: RestoredChatMessage[], contextInfo?: {contextTokens: number; contextWindow: number}, inProgressTurn?: {status: 'running' | 'complete' | 'error' | 'interrupted'} | null) => {
      chatService.cancel()
      streamingMsgIdRef.current = null
      trackedLoadedSkillsRef.current.clear()
      setIsStreaming(false)
      setContextInfo(contextInfo
        ? {contextTokens: contextInfo.contextTokens, contextWindow: contextInfo.contextWindow, compacted: false}
        : {contextTokens: 0, contextWindow: 200_000, compacted: false}
      )
      setActiveOptions(null)
      setActiveDiscoveryQuestion(null)
      setActiveSkillSelection(null)
      clearPendingUploads()
      voiceDraftStoreRef.current = undefined
      setVoiceDestination('main')
      resetCrosstabs()

      // Set the session ID so subsequent messages go to this session
      setSessionId(newSessionId)

      // Load history as chat messages — restore agent + handoff for mascot/card display
      const chatMessages = restoreChatMessages(history)
      setMessages(chatMessages)
      onMessagesChange?.(chatMessages.map(m => ({ role: m.role, content: m.content })))
      onSessionInfoChange?.({ intentSummary: 'Resumed session' })

      // A turn is still running server-side (the user navigated away mid-turn and came
      // back) — reattach: replay the event log, restore the streaming state, tail live.
      if (inProgressTurn?.status === 'running') {
        resumeTurn(newSessionId)
      }
    }
  }))

  // Mirror messages to parent for export
  useEffect(() => {
    if (!onMessagesChange) return
    onMessagesChange(
      messages
        .filter(m => !m.isStreaming)
        .map(m => ({role: m.role, content: m.content}))
    )
  }, [messages, onMessagesChange])

  const handleStop = useCallback(() => {
    chatService.cancel()
    const msgId = streamingMsgIdRef.current
    if (msgId) {
      setMessages(prev =>
        prev.map(m =>
          m.id === msgId
            ? {
                ...m,
                content: m.content + ' *(stopped)*',
                isStreaming: false,
                aborted: true,
                progressSteps: finalizeRunningProgressSteps(m.progressSteps, 'warning'),
              }
            : m
        )
      )
      streamingMsgIdRef.current = null
    }
    streamingContentRef.current = ''
    onStreamingAssistantContent?.(null)
    setIsStreaming(false)
  }, [chatService, onStreamingAssistantContent])

  /**
   * Build the per-turn streaming callbacks. Shared by handleSend (live POST stream) and
   * resumeTurn (reattach via GET /api/r3/chat/stream after a reload mid-turn) — replayed
   * events re-fire these exact handlers, so panels, progress cards, and streaming text
   * restore through the same code that renders them live.
   */
  const buildTurnCallbacks = useCallback((assistantMsgId: string, opts: {isFirstMessage: boolean; initialProgressSteps?: ProgressStepData[]}) => {
    let currentIntent: IntentResult | undefined
    // Accumulate site builder event data in closure vars — merged in onComplete
    // to avoid React batching race (events arrive in same microtask as complete)
    let pendingPlan: SiteBuilderPlanData | undefined
    let pendingOptions: SiteBuilderOptionsData | undefined
    let pendingPreview: {siteId: string | number} | undefined
    let pendingPublished: {url: string; siteId: string | number} | undefined
    let pendingDiscoveryQuestion: DiscoveryQuestionData | undefined
    let pendingSkillLoaded: SkillLoadedData | undefined
    let pendingAppArtifactBuilder: AppArtifactBuilderData | undefined
    let pendingSkillSelection: SkillSelectionData | undefined
    let pendingCrosstabsOptions: CrosstabsOptionsData | undefined
    let pendingCrosstabsObjectives: CrosstabsObjectivesData | undefined
    let pendingCrosstabsFileRequest: CrosstabsFileRequestData | undefined
    let pendingMcpApp: McpAppData | undefined
    let pendingHandoff: {from: string; to: string} | undefined
    let pendingProgressSteps: ProgressStepData[] = opts.initialProgressSteps?.map(step => ({...step})) ?? []

    return {
      onChunk: (chunk: string) => {
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantMsgId
              ? appendAssistantChunk(m, chunk)
              : m
          )
        )
        streamingContentRef.current += chunk
        onStreamingAssistantContent?.(streamingContentRef.current)
      },
      onIntent: (intent: IntentResult) => {
        currentIntent = intent
        setActiveAgent(intent.intent)
        onSessionInfoChange?.({intentSummary: intent.intent, startedAt: Date.now()})
        // Update streaming message immediately so the avatar switches right away
        setMessages(prev =>
          prev.map(m => m.id === assistantMsgId ? {...m, intent} : m)
        )
      },
      onSiteBuilderOptions: (data: SiteBuilderOptionsData) => {
        pendingOptions = data
        setActiveOptions(data)
      },
      onSiteBuilderPlan: (data: SiteBuilderPlanData) => {
        pendingPlan = data
        onSiteBuilderPlanProp?.(data)
      },
      onSiteBuilderGenerating: (siteId: string | number) => {
        onSiteBuilderGenerating?.(siteId)
      },
      onSiteBuilderCodeChunk: (data: SiteBuilderCodeChunkData) => {
        onSiteBuilderCodeChunk?.(data)
      },
      onSiteBuilderPreview: (html: string, siteId: string | number, revision?: number, htmlContent?: string) => {
        pendingPreview = {siteId}
        onSiteBuilderPreview?.(html, siteId, revision, htmlContent)
      },
      onSiteBuilderPublished: (url: string, siteId: string | number) => {
        pendingPublished = {url, siteId}
        onSiteBuilderPublished?.(url, siteId)
      },
      onWebsiteChangeSetReady,
      onMarkdownArtifactPanel,
      onDiscoveryQuestion: (data: DiscoveryQuestionData) => {
        pendingDiscoveryQuestion = data
        setActiveDiscoveryQuestion(data)
      },
      onSkillLoaded: (data: SkillLoadedData) => {
        pendingSkillLoaded = data
        if (!trackedLoadedSkillsRef.current.has(data.name)) {
          trackedLoadedSkillsRef.current.add(data.name)
          trackEvent('ros_skill_loaded', {skill_name: data.name})
        }
      },
      onAppArtifactBuilder: (data: AppArtifactBuilderData) => {
        pendingAppArtifactBuilder = data
        setMessages(prev =>
          prev.map(m => m.id === assistantMsgId ? {...m, appArtifactBuilder: data} : m)
        )
      },
      onAppArtifactOpen: (data: AppArtifactOpenData) => {
        onAppArtifactOpenProp?.(data)
      },
      onSkillSelection: (data: SkillSelectionData) => {
        pendingSkillSelection = data
        setActiveSkillSelection(data)
      },
      onCrosstabsProgress: crosstabsCallbacks.onCrosstabsProgress,
      onCrosstabsClassification: (data: CrosstabsClassificationData) => {
        crosstabsCallbacks.onCrosstabsClassification(data)
        onCrosstabsClassificationProp?.(data)
      },
      onCrosstabsObjectives: (data: CrosstabsObjectivesData) => {
        pendingCrosstabsObjectives = data
        crosstabsCallbacks.onCrosstabsObjectives(data)
        onCrosstabsObjectivesProp?.(data)
      },
      onCrosstabsPlan: (data: CrosstabsPlanData) => {
        crosstabsCallbacks.onCrosstabsPlan(data)
        onCrosstabsPlanProp?.(data)
      },
      onCrosstabsOptions: (data: CrosstabsOptionsData) => {
        pendingCrosstabsOptions = data
        crosstabsCallbacks.onCrosstabsOptions(data)
      },
      onCrosstabsFileRequest: (data: CrosstabsFileRequestData) => {
        pendingCrosstabsFileRequest = data
        crosstabsCallbacks.onCrosstabsFileRequest(data)
      },
      onAuthoringStep: (data: ProgressStepData) => {
        // The first server-issued activity replaces the optimistic planning row. Further events
        // upsert by their stable server IDs, preserving one chronological activity trail.
        pendingProgressSteps = replaceProvisionalPlanningStep(pendingProgressSteps, data)
        // Apply immediately so steps render live during streaming
        setMessages(prev =>
          prev.map(m => m.id === assistantMsgId ? {...m, progressSteps: [...pendingProgressSteps]} : m)
        )
      },
      onAuthoringIteration: (data: AuthoringIterationData) => {
        // Convert iteration event to a progress step
        const step: ProgressStepData = {
          stepId: 'generation_iteration',
          status: data.issueCount > 0 ? 'warning' : 'running',
          name: `Generation — iteration ${data.iteration}/${data.maxIterations}`,
          ...(data.issueCount > 0 && {
            description: `${data.issueCount} issue${data.issueCount > 1 ? 's' : ''} found`,
            substeps: data.issues.map((issue, i) => ({name: issue, status: 'warning', result: ''})),
          }),
        }
        pendingProgressSteps = replaceProvisionalPlanningStep(pendingProgressSteps, step)
        setMessages(prev =>
          prev.map(m => m.id === assistantMsgId ? {...m, progressSteps: [...pendingProgressSteps]} : m)
        )
      },
      onAuthoringVerify: (data: AuthoringVerifyData) => {
        // Convert verification event to a progress step with substeps per pass
        const step: ProgressStepData = {
          stepId: `verify_${data.type}`,
          status: data.passed ? 'complete' : 'warning',
          name: data.type === 'semantic' ? 'Quality Checks' : data.type === 'runtime' ? 'Test Run' : `Verification: ${data.type}`,
          substeps: [
            ...(data.passes ?? []).map(p => ({
              name: p.name,
              status: p.passed ? 'complete' : 'warning',
            })),
            ...(data.tracks ?? []).map(t => ({
              name: t.name,
              status: t.passed ? 'complete' : 'warning',
              result: `${t.cardCount} cards → ${t.terminal}`,
            })),
          ],
        }
        pendingProgressSteps = replaceProvisionalPlanningStep(pendingProgressSteps, step)
        setMessages(prev =>
          prev.map(m => m.id === assistantMsgId ? {...m, progressSteps: [...pendingProgressSteps]} : m)
        )
      },
      onAuthoringStepUpdate: (data: ProgressStepUpdateData) => {
        const index = pendingProgressSteps.findIndex(s => s.stepId === data.stepId)
        if (index >= 0) {
          pendingProgressSteps[index] = mergeProgressStepUpdate(pendingProgressSteps[index], data)
          setMessages(prev =>
            prev.map(m => m.id === assistantMsgId ? {...m, progressSteps: [...pendingProgressSteps]} : m)
          )
        }
      },
      onMcpApp: (data: McpAppData) => {
        pendingMcpApp = data
        onMcpAppProp?.(data)
      },
      onMicroAppOpen: (data: MicroAppOpenData) => {
        onMicroAppOpenProp?.(data)
      },
      onLocationRequest: (data: LocationRequestData) => {
        onLocationRequestProp?.(data)
      },
      onMcpAppInput: (data: McpAppInputData) => {
        setPendingMcpAppInput(data)
      },
      onHandoff: (data: {from: string; to: string; reason: string; summary: string}) => {
        pendingHandoff = {from: data.from, to: data.to ?? 'general'}
        setActiveAgent(data.to === 'general' ? undefined : data.to)
        if (data.to && data.to !== 'general') {
          trackEvent('agent_tool_called', {agent_name: data.to})
        }
        // Apply handoff immediately so the card renders during streaming
        setMessages(prev =>
          prev.map(m => m.id === assistantMsgId ? {...m, handoff: pendingHandoff} : m)
        )
      },
      onAgentModel: (data: AgentModelData) => {
        setActiveModel(data)
      },
      onComplete: (newSessionId: string, tokenUsage?: TokenUsage, info?: {contextTokens: number; contextWindow: number; compacted: boolean}) => {
        streamingMsgIdRef.current = null
        streamingContentRef.current = ''
        clearCrosstabsProgress() // drop any "Computing… (Ns)" line now the turn ended
        onStreamingAssistantContent?.(null)
        setSessionId(newSessionId)
        if (opts.isFirstMessage && onSessionCreated) {
          onSessionCreated(newSessionId)
        }
        if (info) setContextInfo(info)
        if (tokenUsage && tokenUsage.totalTokens > 0) {
          const freshInputTokens = tokenUsage.inputTokens - tokenUsage.cacheReadTokens - tokenUsage.cacheWriteTokens
          trackEvent('token_usage', {
            session_id: newSessionId,
            agent_name: currentIntent?.intent ?? 'general',
            input_tokens: freshInputTokens,
            cache_read_tokens: tokenUsage.cacheReadTokens,
            cache_write_tokens: tokenUsage.cacheWriteTokens,
            output_tokens: tokenUsage.outputTokens,
            total_tokens: tokenUsage.totalTokens
          })
        }
        // Single setMessages call merges all accumulated event data
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantMsgId
              ? {
                  ...m,
                  isStreaming: false,
                  intent: currentIntent,
                  ...(pendingPlan && {siteBuilderPlan: pendingPlan}),
                  ...(pendingOptions && {siteBuilderOptions: pendingOptions}),
                  ...(pendingPreview && {siteBuilderPreview: pendingPreview}),
                  ...(pendingPublished && {siteBuilderPublished: pendingPublished}),
                  ...(pendingDiscoveryQuestion && {discoveryQuestion: pendingDiscoveryQuestion}),
                  ...(pendingSkillLoaded && {skillLoaded: pendingSkillLoaded}),
                  ...(pendingAppArtifactBuilder && {appArtifactBuilder: pendingAppArtifactBuilder}),
                  ...(pendingSkillSelection && {skillSelection: pendingSkillSelection}),
                  ...(pendingCrosstabsOptions && {crosstabsOptions: pendingCrosstabsOptions}),
                  ...(pendingCrosstabsObjectives && {crosstabsObjectives: pendingCrosstabsObjectives}),
                  ...(pendingCrosstabsFileRequest && {crosstabsFileRequest: pendingCrosstabsFileRequest}),
                  ...(pendingMcpApp && {mcpApp: pendingMcpApp}),
                  ...(pendingHandoff && {handoff: pendingHandoff}),
                  ...(pendingProgressSteps.length > 0 && {
                    progressSteps: finalizeRunningProgressSteps(pendingProgressSteps, 'complete'),
                  })
                }
              : m
          )
        )
        setIsStreaming(false)
      },
      onError: (err: Error) => {
        streamingMsgIdRef.current = null
        streamingContentRef.current = ''
        onStreamingAssistantContent?.(null)
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantMsgId
              ? {
                  ...m,
                  content: `Sorry, something went wrong: ${err.message}`,
                  isStreaming: false,
                  failed: true,
                  progressSteps: finalizeRunningProgressSteps(m.progressSteps, 'failed'),
                }
              : m
          )
        )
        setIsStreaming(false)
      }
    }
  }, [onSessionCreated, onSiteBuilderPlanProp, onSiteBuilderGenerating, onSiteBuilderCodeChunk, onSiteBuilderPreview, onSiteBuilderPublished, onWebsiteChangeSetReady, onMarkdownArtifactPanel, onStreamingAssistantContent, onSessionInfoChange, crosstabsCallbacks, onCrosstabsClassificationProp, onCrosstabsObjectivesProp, onCrosstabsPlanProp, onMcpAppProp, onAppArtifactOpenProp, onMicroAppOpenProp, onLocationRequestProp, clearCrosstabsProgress])

  /**
   * Reattach to a turn still running server-side (user reloaded / second tab). The server
   * replays the durable event log from seq 0, then live-tails — the replayed events flow
   * through the same buildTurnCallbacks handlers as a live send, rebuilding streaming text,
   * progress cards, and panels, and ending naturally on the turn's `complete` event.
   */
  const resumeTurn = useCallback((sid: string) => {
    if (!chatService.resumeStream) return   // transport without reattach support (e.g. desktop)

    setIsStreaming(true)
    const assistantMsgId = uuidv4()
    const planningStep = createProvisionalPlanningStep()
    streamingMsgIdRef.current = assistantMsgId
    streamingContentRef.current = ''
    setMessages(prev => [
      ...prev,
      {id: assistantMsgId, role: 'assistant', content: '', isStreaming: true, progressSteps: [planningStep]}
    ])

    void chatService.resumeStream({
      sessionId: sid,
      after: 0,
      ...buildTurnCallbacks(assistantMsgId, {isFirstMessage: false, initialProgressSteps: [planningStep]}),
    }).catch(() => {
      // Reattach is best-effort: on failure, fall back to the at-rest view.
      streamingMsgIdRef.current = null
      setMessages(prev => prev.filter(m => m.id !== assistantMsgId || m.content))
      setIsStreaming(false)
    })
  }, [chatService, buildTurnCallbacks])

  const handleSend = useCallback(async (overrideText?: string, options?: R3SendMessageOptions) => {
    const text = (overrideText ?? inputValueRef.current).trim()
    const pendingFileScope = options?.pendingFileScope ?? 'main'
    const filesForTurn = pendingFiles.filter((file): file is PendingChatFile & {id: string} => file.scope === pendingFileScope && !!file.id)
    const hasFiles = filesForTurn.length > 0
    if (pendingFiles.some(file => file.scope === pendingFileScope && file.uploading)) return
    if ((!text && !hasFiles) || isStreaming) return

    // Ensure a session ID exists (CF backend needs it for DO routing)
    const sid = sessionId ?? uuidv4()
    if (!sessionId) {
      setSessionId(sid)
      trackEvent('r3_chat_created', {
        user_department: department,
      })
    }

    trackEvent('r3_chat_message_sent', {
      session_id: sid,
      user_department: department,
    })

    if (options?.draftStore && options.draftStore !== composerDraftStore) options.draftStore.setValue('')
    else setInputValue('')
    setActiveOptions(null) // Dismiss options widget on send
    setActiveDiscoveryQuestion(null) // Dismiss discovery question widget on send
    setActiveSkillSelection(null) // Dismiss skill selection widget on send
    dismissCrosstabsOptions()
    dismissCrosstabsObjectives()
    dismissCrosstabsFileRequest()

    const attachments: ChatAttachment[] = filesForTurn.map(file => ({
      id: file.id,
      name: file.name,
      mimeType: file.mimeType,
      sizeBytes: file.sizeBytes,
      url: file.url,
    }))
    const targetReferences = options?.websiteTargetReferences ?? websiteTargetReferences ?? []
    const sendWebsiteEditContext = options?.websiteEditContext ?? websiteEditContext

    // Add user message
    const userMsg: ChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: text,
      ...(attachments.length ? {attachments} : {}),
      ...(targetReferences.length ? {websiteTargetReferences: targetReferences} : {}),
    }
    setMessages(prev => [...prev, userMsg])
    setIsStreaming(true)
    broadcastTurnStarted(sid)

    // Placeholder for streaming assistant message
    const assistantMsgId = uuidv4()
    const planningStep = createProvisionalPlanningStep()
    streamingMsgIdRef.current = assistantMsgId
    setMessages(prev => [
      ...prev,
      {id: assistantMsgId, role: 'assistant', content: '', isStreaming: true, progressSteps: [planningStep]}
    ])

    // Build history for context (exclude the placeholder we just added)
    const history = messages.map(m => ({role: m.role, content: m.content}))

    // Collect and clear pending file IDs
    const fileIds = filesForTurn.map(file => file.id)
    setPendingFiles(current => current.filter(file => file.scope !== pendingFileScope))

    try {
      await chatService.sendMessage({
        message: text,
        sessionId: sid,
        department: department || undefined,
        history,
        attachedFileIds: fileIds.length > 0 ? fileIds : undefined,
        attachments: attachments.length > 0 ? attachments.map(({url: _url, ...attachment}) => attachment) : undefined,
        websiteEditContext: sendWebsiteEditContext,
        websiteTargetReferences: targetReferences.length ? targetReferences : undefined,
        ...buildTurnCallbacks(assistantMsgId, {isFirstMessage: !sessionId, initialProgressSteps: [planningStep]}),
      })
    } catch (err) {
      streamingMsgIdRef.current = null
      // If this was an abort, the handleStop already finalized the message
      if ((err as Error)?.name === 'AbortError') return
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMsgId
            ? {
                ...m,
                content: 'Sorry, something went wrong. Please try again.',
                isStreaming: false,
                failed: true,
                progressSteps: finalizeRunningProgressSteps(m.progressSteps, 'failed'),
              }
            : m
        )
      )
      setIsStreaming(false)
    }
  }, [isStreaming, messages, sessionId, chatService, buildTurnCallbacks, pendingFiles, websiteEditContext, websiteTargetReferences, composerDraftStore, dismissCrosstabsOptions, dismissCrosstabsObjectives, dismissCrosstabsFileRequest])




  const handleOptionsSubmit = useCallback((formattedMessage: string) => {
    handleSend(formattedMessage)
  }, [handleSend])

  const handleFileAttach = useCallback(async (files: File[], scope: 'main' | 'contextual' = 'main') => {
    if (!uploadFile) return
    trackEvent('r3_chat_document_uploaded', {
      file_count: files.length,
      user_department: department,
    })
    const sid = sessionId ?? uuidv4()
    if (!sessionId) setSessionId(sid)

    for (const file of files) {
      const clientId = crypto.randomUUID()
      const url = URL.createObjectURL(file)
      const controller = new AbortController()
      uploadControllersRef.current.set(clientId, controller)
      localAttachmentUrlsRef.current.add(url)
      setPendingFiles(prev => [...prev, {
        clientId,
        name: file.name,
        uploading: true,
        mimeType: file.type || undefined,
        sizeBytes: file.size,
        url,
        scope,
      }])

      try {
        const fileId = await uploadFile(sid, file, controller.signal)
        setPendingFiles(prev =>
          prev.map(pending => pending.clientId === clientId ? {...pending, id: fileId, uploading: false} : pending)
        )
      } catch (err) {
        if (controller.signal.aborted || (err instanceof Error && err.name === 'AbortError')) continue
        console.error('File upload failed:', err)
        trackEvent('r3_chat_error', {
          error_type: 'file_upload_failed',
          error_message: err instanceof Error ? err.message : 'File upload failed',
          user_department: department,
        })
        setPendingFiles(prev =>
          prev.map(pending => pending.clientId === clientId ? {...pending, uploading: false} : pending)
        )
      } finally {
        uploadControllersRef.current.delete(clientId)
      }
    }
  }, [uploadFile, sessionId, department])

  const handleRemoveFile = useCallback((clientId: string) => {
    uploadControllersRef.current.get(clientId)?.abort()
    uploadControllersRef.current.delete(clientId)
    setPendingFiles(previous => {
      const removed = previous.find(file => file.clientId === clientId)
      releaseLocalAttachmentUrl(removed?.url)
      return previous.filter(file => file.clientId !== clientId)
    })
  }, [releaseLocalAttachmentUrl])

  const handleApprovePlan = useCallback(async (message: string) => {
    try {
      await approvePlanAndStartBuilding({
        sessionId,
        message,
        approvePlan: onApprovePlan,
        sendMessage: handleSend,
      })
    } catch (err) {
      console.error('Failed to approve website plan:', err)
      trackEvent('r3_chat_error', {
        error_type: 'site_builder_plan_approval_failed',
        error_message: err instanceof Error ? err.message : 'Plan approval failed',
        user_department: department,
      })
      throw err
    }
  }, [onApprovePlan, sessionId, handleSend, department])

  // Stable MessageList callbacks — inline arrows here would change identity every render and defeat
  // MessageList's memo (re-rendering the whole thread on each keystroke). Keep them referentially stable.
  const mcpPanelMode = !!onMcpAppProp
  const handleMcpMessage = useCallback((text: string) => { handleSend(text) }, [handleSend])
  const handleMcpAppInputConsumed = useCallback(() => setPendingMcpAppInput(null), [])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.default'
      }}
    >
      {/* Header bar — only when the host passes headerActions. Ros consolidates these actions into
          its own ChatHeader row, so it passes none and this bar (and its divider) doesn't render. */}
      {headerActions && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 1.5,
            py: 0.75,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
            minHeight: 44
          }}
        >
          {headerActions}
        </Box>
      )}

      {/* Context window status */}
      <ContextWindowBar
        contextTokens={contextInfo.contextTokens}
        contextWindow={contextInfo.contextWindow}
        compacted={contextInfo.compacted}
      />

      {/* Messages */}
      <MessageList
        messages={messages}
        isStreaming={isStreaming}
        reachyAvatarUrl={reachyAvatarUrl}
        userName={userName}
        /* The empty-state hero renders the SAME MessageInput centered in the middle (so attach/mic/
           voice work from the first message); the started view renders it pinned at the bottom. One
           instance, no forked input logic. */
        inputSlot={
          <MessageInput
            variant="hero"
            apiRef={inputApiRef}
            draftStore={composerDraftStore}
            contextChips={websiteTargetChips}
            onDraftChange={handleDraftChange}
            onSend={() => handleSend()}
            onStop={handleStop}
            disabled={isStreaming}
            isStreaming={isStreaming}
            onAttach={uploadFile ? handleFileAttach : undefined}
            onRemoveFile={handleRemoveFile}
            onMic={fetchVoiceToken ? handleMicToggle : undefined}
            voiceDisabled={voiceDestination === 'external' && voiceStatus !== 'idle'}
            voiceDisabledReason="Voice input is active in the website editor"
            onVoiceAccept={handleVoiceAccept}
            onVoiceCancel={handleVoiceCancel}
            audioLevelRef={voiceLevelRef}
            isRecording={voiceDestination === 'main' && voiceStatus === 'recording'}
            isVoiceConnecting={voiceDestination === 'main' && voiceStatus === 'connecting'}
            voiceTranscript={voiceDestination === 'main' ? voiceTranscript : ''}
            pendingFiles={pendingFiles.filter(file => file.scope === 'main')}
            modelLabel={devModelLabel}
            availableModels={devMode ? availableModels : undefined}
            onSelectModel={devMode && sessionId && onSetModelOverride ? handleSelectModel : undefined}
          />
        }
        activeAgent={activeAgent}
        devMode={devMode}
        onSend={handleSend}
        onApprovePlan={handleApprovePlan}
        progressMessage={crosstabsState.progress ? crosstabsState.progress.message : null}
        apiBaseUrl={apiBaseUrl}
        sessionId={sessionId}
        mcpPanelMode={mcpPanelMode}
        onMcpMessage={handleMcpMessage}
        pendingMcpAppInput={pendingMcpAppInput}
        onMcpAppInputConsumed={handleMcpAppInputConsumed}
        onDownloadAppArtifactBuilder={onDownloadAppArtifactBuilder}
      />

      {/* Interactive prompt widgets (site/crosstabs options, discovery questions, skill picker,
          objectives) float above the input. Constrained to the same 820px content column as the
          message thread, header, and input so they don't span the full pane. Only one is active at
          a time, so a single shared column wrapper covers them all. */}
      {(activeOptions || activeDiscoveryQuestion || activeSkillSelection || crosstabsState.activeOptions || crosstabsState.activeObjectives) && (
        <Box sx={{ px: 3, flexShrink: 0 }}>
          <Box sx={{ maxWidth: 820, mx: 'auto' }}>
            {/* Site Builder Options Widget */}
            {activeOptions && (
              <SiteBuilderOptionsWidget
                data={activeOptions}
                onSubmit={handleOptionsSubmit}
              />
            )}

            {/* Discovery Question Widget */}
            {activeDiscoveryQuestion && (
              <DiscoveryQuestionWidget
                data={activeDiscoveryQuestion}
                onSubmit={handleOptionsSubmit}
              />
            )}

            {/* Skill Selection Widget — shown when 2+ skills could match */}
            {activeSkillSelection && (
              <SkillSelectionWidget
                data={activeSkillSelection}
                onSubmit={handleOptionsSubmit}
              />
            )}

            {/* Crosstabs Options Widget — same shape as SiteBuilderOptionsWidget */}
            {crosstabsState.activeOptions && (
              <SiteBuilderOptionsWidget
                data={{question: crosstabsState.activeOptions.question, type: crosstabsState.activeOptions.type, options: (crosstabsState.activeOptions.options ?? []).map(o => ({id: o.id, label: o.label, description: o.description}))}}
                onSubmit={handleOptionsSubmit}
              />
            )}

            {/* Crosstabs Objectives Widget — multi-select checklist */}
            {crosstabsState.activeObjectives && (
              <ObjectivesWidget
                data={crosstabsState.activeObjectives}
                onSubmit={handleOptionsSubmit}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Input — hidden in the empty state, where the centered hero carries its own input (mock).
          Appears once a conversation begins. Centered to the same 820px content column as the
          message thread, with a light hairline above it spanning the column (not edge-to-edge). */}
      {messages.length > 0 && (
        <Box sx={{ flexShrink: 0, px: 3, pb: 1.5, bgcolor: 'background.default' }}>
          <Box sx={{ maxWidth: 820, mx: 'auto', borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
            <MessageInput
              apiRef={inputApiRef}
              draftStore={composerDraftStore}
              contextChips={websiteTargetChips}
              onDraftChange={handleDraftChange}
              onSend={() => handleSend()}
              onStop={handleStop}
              disabled={isStreaming}
              isStreaming={isStreaming}
              onAttach={uploadFile ? handleFileAttach : undefined}
              onRemoveFile={handleRemoveFile}
              onMic={fetchVoiceToken ? handleMicToggle : undefined}
              voiceDisabled={voiceDestination === 'external' && voiceStatus !== 'idle'}
              voiceDisabledReason="Voice input is active in the website editor"
              onVoiceAccept={handleVoiceAccept}
              onVoiceCancel={handleVoiceCancel}
              audioLevelRef={voiceLevelRef}
              isRecording={voiceDestination === 'main' && voiceStatus === 'recording'}
              isVoiceConnecting={voiceDestination === 'main' && voiceStatus === 'connecting'}
              voiceTranscript={voiceDestination === 'main' ? voiceTranscript : ''}
              pendingFiles={pendingFiles.filter(file => file.scope === 'main')}
              modelLabel={devModelLabel}
              availableModels={devMode ? availableModels : undefined}
              onSelectModel={devMode && sessionId && onSetModelOverride ? handleSelectModel : undefined}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
})

R3Chat.displayName = 'R3Chat'

export default R3Chat
